/**
 * Route table — declarative createBrowserRouter for Phase 1.
 *
 * Public branch (AuthLayout):
 *   /login          → LoginPage
 *   /invite/:token  → InviteAcceptPage
 *   /reset/:token   → ResetPasswordPage
 *
 * Protected branch (RequireAuth + RootLayout):
 *   /                       → RoleHomeRedirect (→ /workload Phase 1)
 *   /workload               → WorkloadPage (Phase 3 W4.T1 — replaces stub)
 *   /profile                → ProfilePage
 *   /force-password-change  → ForcePasswordChangePage (V3-FIX-NEW-5)
 *
 * Catch-alls:
 *   /forbidden      → ForbiddenPage (under RootLayout when authed,
 *                     AuthLayout otherwise — wired here under
 *                     RootLayout because Phase 1 only triggers it from
 *                     authed paths; unauth users hitting it just see
 *                     the same content because layout chrome is light)
 *   *               → NotFoundPage
 *
 * Phase 6 W4 T4.1 (R2 SR-1 + NIT-1 + NIT-4):
 *   /workload, /profile, /profile/:userId, /supervisor are now lazy
 *   loaded with per-route Suspense skeletons (variant prop matches
 *   destination shape — workload heatmap, supervisor form, profile
 *   tab strip). Initial bundle drops from ~894 KB to ≤ 850 KB.
 *
 *   NIT-1 — intra-route transitions inside /workload (heatmap ↔
 *   calendar tab) MUST remain synchronous. The page is the lazy
 *   boundary; its sub-components are NOT React.lazy and MUST stay
 *   bundled together so tab switching has no chunk-load delay.
 *   Do NOT wrap WorkloadPage sub-components in React.lazy.
 */
import * as React from 'react';
import { createBrowserRouter, type RouteObject } from 'react-router-dom';

import { RequireAuth } from '@/components/auth/require-auth';
import { RequireAccessLevel } from '@/components/auth/require-access-level';
import { RequireRightsManager } from '@/components/auth/require-rights-manager';
import { RoleHomeRedirect } from '@/components/auth/role-home-redirect';
import { RouteErrorBoundary } from '@/components/auth/route-error-boundary';
import { RouteSkeleton, type SkeletonVariant } from '@/components/auth/route-skeleton';
import { AuthLayout } from '@/layouts/auth-layout';
import { RootLayout } from '@/layouts/root-layout';
import { LoginPage } from '@/pages/login/login.page';
import { InviteAcceptPage } from '@/pages/invite/invite-accept.page';
import { ResetPasswordPage } from '@/pages/reset/reset-password.page';
import { ForcePasswordChangePage } from '@/pages/force-password-change/force-password-change.page';
import { ForbiddenPage } from '@/pages/errors/forbidden.page';
import { NotFoundPage } from '@/pages/errors/not-found.page';
import { EmployeesListPage } from '@/pages/employees/employees-list.page';
import { ROUTE_ACCESS } from '@/lib/route-access';

// Phase-4 W4.T4 — lazy-loaded route bundles. Vite emits a separate chunk
// per `lazy()` import, so /projects, /projects/:id, /clients each ship
// their own JS file rather than bloating the initial bundle. Default
// export from each page module is required for `React.lazy`.
const ProjectsListPage = React.lazy(() => import('@/pages/projects/projects-list.page'));
const ProjectsDetailPage = React.lazy(() => import('@/pages/projects/projects-detail.page'));
const ClientsListPage = React.lazy(() => import('@/pages/clients/clients-list.page'));
// Блок 2 · Ф7 · В11-b — справочник договоров. Ленивый по тому же лекалу, что
// соседние справочники: экран открывают редко, и держать его в начальном
// наборе значило бы платить за него на каждом входе.
const ContractsListPage = React.lazy(() => import('@/pages/contracts/contracts-list.page'));

// Phase-5 W3.T3.2 — /dashboard lazy bundle. Hidden behind the
// MANAGER_AND_ADMIN gate (route-level defence) and additionally
// hidden in the sidebar for EMPLOYEE + SUPERVISOR (root-layout.tsx).
const DashboardPage = React.lazy(() => import('@/pages/dashboard/dashboard.page'));

// Phase-6 W4 T4.1 — three remaining hot routes split out of the initial
// bundle. The pages use NAMED exports (legacy contract — tests import
// `{ WorkloadPage }` etc. directly), so the dynamic import adapter
// shapes the module namespace into the `{ default }` form React.lazy
// requires. Keeping the named exports intact avoids touching dozens of
// test files (~1015+ tests rely on the names).
const WorkloadPage = React.lazy(() =>
  import('@/pages/workload/workload.page').then((m) => ({ default: m.WorkloadPage })),
);
// Блок 2 · Ф4 · В5 — личный экран «Моя работа». Ленивый по тому же лекалу, что
// и соседи. Гейт уровня — тот же, что у `/workload` (EMPLOYEE + MANAGER +
// ADMIN): супервайзеру `GET /works/mine` отвечает 403 по атому `work.own.read`
// (`work.service.ts`), и маршрут это зеркалит, чтобы отказ пришёл экраном, а не
// пустым списком.
const MyWorkPage = React.lazy(() =>
  import('@/pages/my-work/my-work.page').then((m) => ({ default: m.MyWorkPage })),
);
// Блок 2 · Ф4 · В6-б — экран задач `/tasks` (Р-19). Ленивый по тому же лекалу,
// что и соседи, и здесь это не гигиена: подволна (в) ставит на этот экран
// `@dnd-kit`, а начальный бандл держит потолок гейтом `check-bundle-size.mjs`.
// Новая зависимость обязана уехать в ленивый кусок, а не в старт.
const TasksPage = React.lazy(() =>
  import('@/pages/tasks/tasks.page').then((m) => ({ default: m.TasksPage })),
);
const ProfilePage = React.lazy(() =>
  import('@/pages/profile/profile.page').then((m) => ({ default: m.ProfilePage })),
);
const SupervisorSettingsPage = React.lazy(() =>
  import('@/pages/supervisor/supervisor-settings.page').then((m) => ({
    default: m.SupervisorSettingsPage,
  })),
);
// Block 2 «Трекер объектов», Этап 2 · W2a · T2b — the §5.2 catalog editor.
const SupervisorCatalogPage = React.lazy(() =>
  import('@/pages/supervisor/catalog.page').then((m) => ({
    default: m.SupervisorCatalogPage,
  })),
);
// Блок 2 · Ф8 · В-Э — сводный список прав (Р-200). Ленивый по тому же лекалу,
// что соседние экраны супервайзера: открывает его один человек в бюро.
const SupervisorRightsPage = React.lazy(() =>
  import('@/pages/supervisor/rights.page').then((m) => ({
    default: m.SupervisorRightsPage,
  })),
);

/**
 * Wrap a lazy-loaded element in:
 *   1. RouteErrorBoundary — catches `ChunkLoadError` from a stale-deploy
 *      manifest mismatch and surfaces a friendly «Повторить» panel.
 *   2. Suspense — shows the RouteSkeleton fallback while the chunk
 *      streams in. Accepts an optional `variant` so per-route fallbacks
 *      match the destination layout (R2 SR-1).
 *
 * NIT-1 reminder: this wraps top-level route components only. Do NOT
 * apply it to sub-pages, modals, or tab content — that turns intra-page
 * transitions (e.g. heatmap ↔ calendar inside /workload) into chunk
 * loads and breaks the «feels instant» contract.
 */
function lazyRoute(
  node: React.ReactElement,
  variant: SkeletonVariant = 'generic',
): React.ReactElement {
  return (
    <RouteErrorBoundary>
      <React.Suspense fallback={<RouteSkeleton variant={variant} />}>{node}</React.Suspense>
    </RouteErrorBoundary>
  );
}

export const routes: RouteObject[] = [
  {
    element: <AuthLayout />,
    children: [
      { path: '/login', element: <LoginPage /> },
      { path: '/invite/:token', element: <InviteAcceptPage /> },
      { path: '/reset/:token', element: <ResetPasswordPage /> },
    ],
  },
  {
    element: (
      <RequireAuth>
        <RootLayout />
      </RequireAuth>
    ),
    children: [
      { index: true, element: <RoleHomeRedirect /> },
      // Phase-6 W4 T4.1 — /workload lazy boundary with workload-shaped
      // skeleton (filter bar + tab strip + 7×8 heatmap). Intra-page
      // transitions stay synchronous; only the cold chunk-load shows
      // the fallback. See module comment for NIT-1 rationale.
      { path: '/workload', element: lazyRoute(<WorkloadPage />, 'workload') },
      // Блок 2 · Ф4 · В5 — «Моя работа». ⚠️ ГЕЙТ УРОВНЯ ЗДЕСЬ, А НЕ ТОЛЬКО В
      // МЕНЮ: пункт меню супервайзеру не показан, но прямой переход по адресу
      // меню не спрашивает. Без этой группы супервайзер увидел бы экран, чей
      // единственный запрос отвечает ему 403.
      //
      // ⚠️ ⚠️ ГРУППА РАЗДЕЛЕНА НА ДВЕ — Р-59 (20.08.2026), И ЭТО НЕ КОСМЕТИКА.
      // Прежде `/my` и `/tasks` делили одну тройку уровней; теперь ГИП (ADMIN)
      // на «Моей работе» ЗАПРЕЩЁН, а на «Поручениях» — законен, и одним
      // списком это уже не выражается.
      //
      // Основание Р-59: исполнителем работы может быть только человек С
      // ОТДЕЛОМ, а CHECK `user_dept_match_role` (миграция 002) требует у ADMIN
      // и SUPERVISOR отдел ПУСТОЙ. Экран у них пуст ПО ПОСТРОЕНИЮ.
      //
      // ⚠️ ЗАКРЫВАЕТСЯ МАРШРУТ, А НЕ ПРЯЧЕТСЯ ПУНКТ — граница 2 Р-59 дословно:
      // «прямой заход по адресу `/my` для этих уровней не должен открывать
      // пустой экран — иначе пункт убран, а беда осталась». Скрытие пункта
      // живёт в `layouts/root-layout.tsx` (`EMPLOYEE_AND_MANAGER`) и рубежом НЕ
      // является; рубеж здесь.
      {
        element: <RequireAccessLevel allowedAccessLevels={ROUTE_ACCESS.myWorks} />,
        children: [{ path: '/my', element: lazyRoute(<MyWorkPage />) }],
      },
      // Блок 2 · Ф4 · В6-б — `/tasks`. Тройка уровней здесь не по аналогии с
      // соседом, а по правилу: супервайзер задач не ставит и не получает
      // (следствие (б) Р-16 — `assert-assignees.ts`, первая строка
      // `canAssignTo`), а оба источника экрана отвечают ему 403
      // (`work.own.read` / `work.authored.read`).
      // ⚠️ ГЕЙТ НА МАРШРУТЕ, А НЕ ТОЛЬКО В МЕНЮ: пункт меню супервайзеру не
      // показан, но прямой переход по адресу меню не спрашивает.
      //
      // ⚠️ `ADMIN` ЗДЕСЬ ОСТАЁТСЯ, И ЭТО ГРАНИЦА 1 Р-59, А НЕ НЕДОСМОТР:
      // «Поручения» — тот самый путь, которым ГИП заводит задачу другому
      // (кнопка «Новая задача» → `task-form.modal.tsx` → поле «Исполнитель»).
      // Убери `ADMIN` заодно с «Моей работой» — и решение, снявшее пустой
      // экран, отняло бы живое действие.
      {
        element: <RequireAccessLevel allowedAccessLevels={ROUTE_ACCESS.tasks} />,
        children: [{ path: '/tasks', element: lazyRoute(<TasksPage />) }],
      },
      // Phase-3 W4.T6 (D01): /profile is the unified user-settings entry.
      // `/profile` and `/profile/me` both render the actor's own profile;
      // `/profile/:userId` renders another user's read-only personal info
      // (BE enforces RBAC, supervisor → 403 → /forbidden via the fetcher).
      // Phase-6 W4 T4.1: lazy with profile-shaped skeleton (4 tabs + 5 fields).
      { path: '/profile', element: lazyRoute(<ProfilePage />, 'profile') },
      { path: '/profile/:userId', element: lazyRoute(<ProfilePage />, 'profile') },
      { path: '/force-password-change', element: <ForcePasswordChangePage /> },
      { path: '/forbidden', element: <ForbiddenPage /> },
      // Phase-2 Wave-3 W3.T5: /employees gated via `allowedAccessLevels`
      // (NOT minAccessLevel).
      //
      // V2-FIX-07 narrowed 2026-06-10: SUPERVISOR is now allowed on
      // /employees ONLY (supervisor-invites-admin feature); /projects,
      // /clients, /dashboard remain SUPERVISOR-blocked. /employees is
      // therefore split into its own MANAGER+ADMIN+SUPERVISOR group while
      // the rest keep the original MANAGER+ADMIN gate.
      //
      // Group A — /employees: MANAGER + ADMIN + SUPERVISOR. SUPERVISOR
      // gets read access plus ADMIN-tier invite/reissue/hard-delete
      // (RBAC enforced server-side; the page + columns mirror it).
      {
        element: <RequireAccessLevel allowedAccessLevels={['MANAGER', 'ADMIN', 'SUPERVISOR']} />,
        children: [{ path: '/employees', element: <EmployeesListPage /> }],
      },
      // Group B — /projects, /projects/:id, /clients, /dashboard:
      // MANAGER + ADMIN only. SUPERVISOR (Q18) and EMPLOYEE (Q10) are
      // blocked at the route level and bounce to /forbidden — the sidebar
      // additionally hides the nav links (defence-in-depth, see
      // root-layout.tsx W4.T5). Each lazy boundary wraps in
      // RouteErrorBoundary + Suspense (lazyRoute) so a stale-CDN chunk-load
      // failure surfaces «Повторить» instead of a blank screen.
      {
        element: <RequireAccessLevel allowedAccessLevels={ROUTE_ACCESS.projects} />,
        children: [
          { path: '/projects', element: lazyRoute(<ProjectsListPage />) },
          { path: '/projects/:id', element: lazyRoute(<ProjectsDetailPage />) },
          { path: '/clients', element: lazyRoute(<ClientsListPage />) },
          // Блок 2 · Ф7 · В11-b — `/contracts`. Та же пара уровней, что у
          // объектов и заказчиков, и это НЕ аналогия: сервер отвечает на
          // `GET /contracts` руководителю отдела и административному уровню,
          // а рядовому сотруднику и техдиректору — отказом. Сумму внутри
          // ответа сервер ветвит сам; маршрут о ней не знает.
          //
          // ⚠️ РУБЕЖ ЗДЕСЬ, А НЕ ТОЛЬКО В МЕНЮ: пункт сайдбара прячется от
          // тех же уровней, но прямой переход по адресу меню не спрашивает.
          { path: '/contracts', element: lazyRoute(<ContractsListPage />) },
          // Phase-5 W3.T3.2: /dashboard. EMPLOYEE + SUPERVISOR hitting
          // the URL directly hit the RequireAccessLevel guard above and
          // bounce to /forbidden (Q03/Q04 — hard 403, no soft redirect).
          { path: '/dashboard', element: lazyRoute(<DashboardPage />) },
        ],
      },
      // Phase-2 Wave-5 W5.T3: /supervisor is the SUPERVISOR-only settings
      // surface. Explicit allow-list (NOT minAccessLevel) keeps the
      // intent legible and matches the BE RBAC matrix exactly.
      // Phase-6 W4 T4.1: lazy with supervisor-shaped skeleton
      // (4-field form + 2 action rows).
      {
        element: <RequireAccessLevel allowedAccessLevels={['SUPERVISOR']} />,
        children: [
          { path: '/supervisor', element: lazyRoute(<SupervisorSettingsPage />, 'supervisor') },
          // Block 2 · Этап 2 · W2a · T2b — §5.2 catalog tree editor.
          {
            path: '/supervisor/catalog',
            element: lazyRoute(<SupervisorCatalogPage />, 'supervisor'),
          },
          // Блок 2 · Ф8 · В-Э — сводный список прав.
          //
          // ⚠️ ВТОРОЙ РУБЕЖ ВНУТРИ ГРУППЫ, И ОН НЕСУЩИЙ: группа пускает
          // уровень супервайзера, а маршруты прав на сервере открыты только
          // штатному супервайзеру (умолчание 4 Р-211) — должность `CEO` того
          // же уровня получает 403. `RequireRightsManager` сужает группу тем
          // же предикатом, что прячет пункт меню и блок в карточке человека.
          {
            element: <RequireRightsManager />,
            children: [
              {
                path: '/supervisor/rights',
                element: lazyRoute(<SupervisorRightsPage />, 'supervisor'),
              },
            ],
          },
        ],
      },
    ],
  },
  // Public 404 — must be last so it does not shadow other branches.
  { path: '*', element: <NotFoundPage /> },
];

export function createRouter() {
  return createBrowserRouter(routes);
}
